import { useState } from 'react'

import type {
  ProductColumnMatchInfo,
  SelectedColumn,
} from '@Store/types/product'
import ManageTableCheckbox from '@Utils/forms/checkbox/manage-table-checkbox'

import Dropdown from '../../../../components/dropdown'
import type { DropdownOption } from '../../../../components/dropdown'
import type ColumnMappingProps from '../../types/ColumnMappingProps'

const INITIAL_PAGE_LIMIT = 10

function MapFieldsTable({
  properties = [],
  selected,
  allSelected,
  options,
  setSelected,
}: {
  properties?: Partial<ProductColumnMatchInfo>[]
  selected: SelectedColumn[]
  allSelected: SelectedColumn[]
  removeItem: (index: number) => void
  options: ColumnMappingProps[]
  setSelected: (data: SelectedColumn[]) => void
}): JSX.Element {
  const [pagination, setPagination] = useState({
    limit: INITIAL_PAGE_LIMIT,
    page: 1,
  })

  const listedProperties = [
    ...properties.slice(
      (pagination.page - 1) * pagination.limit,
      pagination.page * pagination.limit,
    ),
  ]

  const dropdownOptions: DropdownOption[] = [...options].reduce(
    (acc: DropdownOption[], item) => {
      if (
        item?.multiple ||
        (allSelected ?? []).findIndex((i) => i.key === item.key) === -1
      ) {
        acc.push({
          label: item.text,
          value: item.key,
          ...(item?.id ? { id: item.id } : {}),
        })
      }

      return acc
    },
    [
      {
        label: 'Choose a property',
        value: '',
        disabled: true,
      },
    ],
  )

  const isAllSelected =
    selected?.length > 0 && selected?.every((item) => item?.isSelected)

  const onPropertySelect = (
    property: Partial<ProductColumnMatchInfo>,
    option?: DropdownOption,
  ): void => {
    const propertyIndex = properties.findIndex(
      (p) => p.index === property.index,
    )
    if (propertyIndex === -1) {
      return
    }

    const selectedIndex =
      selected.find((i) => i.index === property.index)?.index ?? -1

    if (selectedIndex > -1 && !option) {
      setSelected(selected.filter((s) => s.index !== selectedIndex))
    } else if (selectedIndex === -1) {
      setSelected([
        ...selected,
        {
          index: property?.index ? Number(property.index) : 0,
          key: option ? option.value : (property.key ?? ''),
          isSelected: true,
          ...(option && 'id' in option ? { id: option.id } : {}),
          ...('value' in property ? { initialValue: property.value } : {}),
          ...('name' in property ? { name: property.name } : {}),
        },
      ])
    } else {
      setSelected(
        selected.map((item) => {
          if (item.index === selectedIndex) {
            return {
              ...item,
              key: option ? option.value : (property.key ?? ''),
            }
          }

          return item
        }),
      )
    }
  }

  const onPropertyMatch = (
    option: DropdownOption,
    property: Partial<ProductColumnMatchInfo>,
  ): void => {
    if (option.value === '') {
      setSelected(selected.filter((i) => i.index !== property.index))
    } else {
      onPropertySelect(property, option)
    }
  }

  const toggleAll = (): void => {
    setSelected(
      isAllSelected
        ? []
        : properties.map((item) => ({
            ...item,
            isSelected: true,
            index: item.index ?? 0,
            key: item.key ?? '',
          })),
    )
  }

  const handlePrev = (): void => {
    setPagination((prev) => ({
      ...prev,
      page: prev.page - 1,
    }))
  }

  const handleNext = (): void => {
    setPagination((prev) => ({
      ...prev,
      page: prev.page + 1,
    }))
  }

  return (
    <div className="border border-solid border-gray-200 rounded-lg mt-8">
      <div className="relative overflow-y-visible w-full">
        <table className="w-full text-left table-fixed">
          <thead className="border-b border-gray-200 font-medium text-sm text-gray-500">
            <tr>
              <th
                scope="col"
                className={`py-3 pr-3 font-medium text-xs text-gray-500 w-[48px]`}
              >
                <div className="flex justify-end w-full [&>button]:mr-0">
                  <ManageTableCheckbox
                    isChecked={isAllSelected}
                    handleCheckboxChange={toggleAll}
                  />
                </div>
              </th>
              <th
                scope="col"
                className={`py-3 font-medium text-xs text-gray-500 w-[180px]`}
              >
                Property taken from data
              </th>
              <th
                scope="col"
                className={`py-3 font-medium text-xs text-gray-500 w-[calc(100%-448px)]`}
              >
                Preview
              </th>
              <th
                scope="col"
                className={`py-3 font-medium text-xs text-gray-500 w-[220px]`}
              >
                Mapped Prisync Property
              </th>
            </tr>
          </thead>
          <tbody>
            {listedProperties.map((property, i) => {
              const propertyMatchedWithSelectedItemKey = allSelected.find(
                (i) => i.index === property.index,
              )?.key
              const propertyMatchedWithOption =
                propertyMatchedWithSelectedItemKey
                  ? options.find(
                      (i) => i.key === propertyMatchedWithSelectedItemKey,
                    )
                  : undefined
              const propertyMatchedWith = propertyMatchedWithOption
                ? {
                    label: propertyMatchedWithOption.text,
                    value: propertyMatchedWithOption.key,
                  }
                : undefined

              return (
                <tr
                  className={`hover:bg-primary-100 border-b border-solid border-gray-200 bg-gray-50`}
                  key={`${property.name}-${property.value}-${property.index}`}
                >
                  <td
                    className={`py-3 pr-3 font-medium text-xs text-gray-500 w-[48px]`}
                  >
                    <div className="flex justify-end w-full [&>button]:mr-0">
                      <ManageTableCheckbox
                        isChecked={
                          selected.find((i) => i.index === property.index)
                            ?.isSelected ?? false
                        }
                        handleCheckboxChange={() => onPropertySelect(property)}
                      />
                    </div>
                  </td>
                  <td className="py-3 font-medium text-sm text-gray-900 w-[180px]">
                    {property.name}
                  </td>
                  <td className="py-3 font-medium text-sm text-gray-500 whitespace-nowrap overflow-hidden text-ellipsis pr-4 w-[calc(100%-448px)]">
                    <span className="text-ellipsis">{property.value}</span>
                  </td>
                  <td className="py-3 font-medium text-sm text-gray-500 pr-2.5 w-[220px]">
                    <Dropdown
                      className="[&>button]:py-1"
                      options={[
                        ...[
                          dropdownOptions[0],
                          ...(propertyMatchedWith ? [propertyMatchedWith] : []),
                          ...dropdownOptions.slice(1),
                        ],
                      ]}
                      disableFormikUpdate={true}
                      defaultSelectedIndex={propertyMatchedWith ? 1 : 0}
                      onChange={(option) => onPropertyMatch(option, property)}
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {properties && properties.length > pagination.limit && (
          <>
            <div className="flex flex-row justify-between container mx-auto px-6 pt-3 pb-4">
              <button
                className="flex items-center justify-center border border-solid border-gray-300 disabled:opacity-50 rounded-lg w-20 h-9"
                onClick={handlePrev}
                {...(pagination?.page <= 1 ? { disabled: true } : {})}
              >
                <span className="font-medium text-sm text-gray-900">
                  Previous
                </span>
              </button>
              <span className="font-medium text-sm text-gray-900 pt-2">
                Page {pagination.page} of{' '}
                {Math.ceil(properties?.length / pagination.limit)}
              </span>
              <button
                className="flex items-center justify-center border border-solid border-gray-300 disabled:opacity-50 rounded-lg w-16 h-9"
                onClick={handleNext}
                {...(pagination?.page >=
                Math.ceil(properties?.length / pagination.limit)
                  ? { disabled: true }
                  : {})}
              >
                <span className="font-medium text-sm text-gray-900">Next</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default MapFieldsTable
